import { GridComparatorFn } from '@mui/x-data-grid-pro'

import { CategoryColors, CategoryGuids, CategoryGuidToName } from '../constants'
import { DetailedCategoryWithTransactions, MonthlyCategoryTotals } from '../types'

type DataPoint = { x: Date; y: number }
type Dataset = DataPoint[]

type CategoryTotalData = {
  top_level_category_guid: string
  category_guid: string
  amount: number
  year_month: number // e.g. 202409
}

type GroupedCategoryTotalData = {
  [categoryGuid: string]: CategoryTotalData[]
}

export interface StackedLineChartData {
  top_level_category_guid: string
  category_guid: string
  category_name: string
  category_color: string
  dataset: Dataset
}

const getCategoryColor = (categoryGuid: string): string => {
  return CategoryColors[categoryGuid] ?? CategoryColors.default
}

const getCategoryName = (categoryGuid: string): string => {
  return CategoryGuidToName[categoryGuid]
}

function createYearMonthRange(start: Date, end: Date): number[] {
  const result = []
  const current = new Date(start)

  while (current <= end) {
    const year = current.getFullYear()
    const month = String(current.getMonth() + 1).padStart(2, '0')
    result.push(Number(`${year}${month}`))
    current.setMonth(current.getMonth() + 1)
  }

  return result
}

const createGroupedCategoryTotals = (totals: MonthlyCategoryTotals[]): GroupedCategoryTotalData => {
  return totals.reduce((result, item) => {
    const totalData: CategoryTotalData = {
      top_level_category_guid: item.top_level_category_guid,
      category_guid: item.category_guid,
      amount: item.total,
      year_month: item.year_month,
    }

    const { top_level_category_guid, category_guid } = totalData

    // Ensure category bucket exists
    if (!result[category_guid]) {
      result[category_guid] = []
    }
    result[category_guid].push(totalData)

    // Ensure top-level category bucket exists (if different than category)
    if (top_level_category_guid !== category_guid) {
      if (!result[top_level_category_guid]) {
        result[top_level_category_guid] = []
      }
      result[top_level_category_guid].push(totalData)
    }

    return result
  }, {} as GroupedCategoryTotalData)
}

export const getStackedLineChartData = (
  totals: MonthlyCategoryTotals[],
  start: Date,
  end: Date,
): StackedLineChartData[] => {
  const groupedTotals = createGroupedCategoryTotals(totals)
  const yearMonths = createYearMonthRange(start, end)

  return Object.entries(groupedTotals).map(([categoryGuid, totals]) => {
    const topLevelCategoryGuid = totals[0].top_level_category_guid
    // Build dataset for current category
    const dataset: Dataset = yearMonths
      // Build chart coordinates and fill missing totals with 0
      .map((yearMonth) => {
        // Sum category totals for same month
        // (totals with same top_level_category_guid but different category_guid)
        // And fill missing totals with 0
        // (necessary for StackedLineChart lines to be smooth)
        const sumTotal = totals
          .filter((total) => total.year_month === yearMonth)
          .reduce((sum, total) => sum + Math.abs(total.amount), 0)

        const year = Number(String(yearMonth).slice(0, 4))
        const month = Number(String(yearMonth).slice(4))
        const date = new Date(year, month - 1)

        return { x: date, y: sumTotal }
      })
      .sort((a, b) => a.x.getTime() - b.x.getTime())

    return {
      top_level_category_guid: topLevelCategoryGuid,
      category_guid: categoryGuid,
      category_name: getCategoryName(categoryGuid),
      category_color: getCategoryColor(topLevelCategoryGuid),
      dataset: dataset,
    }
  })
}

export const amountComparator: GridComparatorFn<string | number> = (v1, v2) => {
  const num1 = typeof v1 === 'string' ? parseFloat(v1.replace(/[^0-9.-]+/g, '')) : v1
  const num2 = typeof v2 === 'string' ? parseFloat(v2.replace(/[^0-9.-]+/g, '')) : v2

  return (num1 || 0) - (num2 || 0)
}

export const categoryComparator: GridComparatorFn<string> = (cat1, cat2) => {
  // Always keep "General" categories at the bottom of the list
  return cat1.indexOf('General') >= 0 ? 1 : cat1.localeCompare(cat2)
}

const buildTooltipLabels = (data: DetailedCategoryWithTransactions, monthsCount: number) => {
  const subcategories = data.subCategories.filter((subcategory) => subcategory.currentAmount !== 0)
  const monthsData = data.totalMonthlyAmounts.slice(-monthsCount)

  const formatMonthLabel = (label: string) => label.substring(0, 3) + ' ' + label.slice(-4)

  return monthsData.map((monthData) => {
    const monthLabel = formatMonthLabel(monthData.label)

    const subcategoryEntries = subcategories.map((subcategory) => {
      const monthEntry = subcategory.monthlyAmounts
        .slice(-monthsCount)
        .find((entry) => formatMonthLabel(entry.label) === monthLabel)

      return {
        label: subcategory.name,
        amount: Math.abs(monthEntry?.amount || 0),
        header: monthLabel,
      }
    })

    const parentMonthEntry = data.monthlyAmounts
      .slice(-monthsCount)
      .find((entry) => formatMonthLabel(entry.label) === monthLabel)

    const parentEntry = {
      label: data.name,
      amount: Math.abs(parentMonthEntry?.amount || 0),
      header: monthLabel,
    }

    return [parentEntry, ...subcategoryEntries].sort((a, b) => b.amount - a.amount)
  })
}

export const buildCategoryDetailsChartData = (
  data: DetailedCategoryWithTransactions | null,
  monthsCount: number,
) => {
  const monthlyAmounts = data
    ? data.totalMonthlyAmounts.slice(-monthsCount).map((monthData) => ({
        x: monthData.label.substring(0, 3),
        y: Math.abs(monthData.amount),
      }))
    : []

  const tooltipLabels = data ? buildTooltipLabels(data, monthsCount) : []

  return { monthlyAmounts, tooltipLabels }
}

export const filterAndSortCategoriesWithTransactions = (
  categories: DetailedCategoryWithTransactions[],
): DetailedCategoryWithTransactions[] => {
  return categories
    .slice()
    .filter(
      (category) =>
        category.totalAmount !== 0 &&
        !category.isTransfer &&
        category.guid !== CategoryGuids.INVESTMENTS,
    )
    .sort((a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount))
}
